import App from "@bloglovin/inspire-engine/App";
import { contextualAppName } from "%/dynamic-component-editor/classes/platform-standard-entities";
import { SmartFeedEntityDynamicElementMixin } from "%/dynamic-component-editor/mixins/smart-feed-entity-dynamic-element-mixin";
import { readBoundContextName, resolveBoundEntityId } from "%/dynamic-component-editor/helpers/bound-context-entity-id";
import {
  EVENT_PREVIEW_STATE_AUTOMATIC,
  EVENT_PREVIEW_STATE_ENDED,
  EVENT_PREVIEW_STATE_LIVE,
  EVENT_PREVIEW_STATE_PRE_EVENT,
  EVENT_PREVIEW_STATE_ROOM_OPEN,
} from "%/dynamic-component-editor/options/event-preview-state";

const EVENT_ENTITY_TYPE = "Event";

const EXAMPLE_EVENT_LEAD_SECONDS = 3600;
const EXAMPLE_EVENT_DURATION_SECONDS = 3600;

function exampleStartTimestamp() {
  return Math.floor(Date.now() / 1000) + EXAMPLE_EVENT_LEAD_SECONDS;
}

const OCCURRENCE_KEY_SEPARATOR = "_";

const EMPTY_EVENT_ENTITY = Object.freeze({
  id: 0,
  title: "",
  description: "",
  image: "",
  location: "",
  meetingLink: "",
  eventType: "",
  ticketPrice: 0,
  eventDate: 0,
  startTime: 0,
  endTime: 0,
  recurringFrequency: 0,
  recurrenceSummary: "",
  publication: null,
  completedAt: 0,
  seriesCompletedAt: 0,
  occurrenceStart: 0,
  isRecurring: false,
  seriesEnded: false,
  alwaysAvailable: false,
  allowEarlyJoin: false,
  earlyJoinMinutes: 0,
  allowEarlyVideo: false,
  isAllDay: false,
  saveMeeting: true,
  chatOpenBeforeMinutes: 0,
  chatOpenAfterMinutes: 0,
});

function projectEntityOntoOccurrence(entity, occurrenceStart) {
  if (!Number.isFinite(occurrenceStart) || occurrenceStart <= 0) {
    return entity;
  }
  const durationSeconds = Math.max(0, Number(entity.endTime || 0) - Number(entity.startTime || 0));

  return {
    ...entity,
    eventDate: occurrenceStart,
    startTime: occurrenceStart,
    endTime: occurrenceStart + durationSeconds,
    occurrenceStart,
  };
}

export const EventCardElementMixin = {
  extends: SmartFeedEntityDynamicElementMixin,
  inject: {
    pageEntities: { default: null },
    useExampleData: { default: () => () => false },
    scopedProvidedContexts: { default: null },
  },
  computed: {
    isPreviewMode() {
      const fn = this.useExampleData;
      return typeof fn === "function" ? Boolean(fn()) : Boolean(fn);
    },
    boundContextName() {
      return readBoundContextName(this);
    },
    eventEntityId() {
      return resolveBoundEntityId(this, "event");
    },
    hasResolvedEventEntity() {
      const id = this.eventEntityId;
      if (!id) {
        return false;
      }
      const entity = App.get(contextualAppName()).getEntity(EVENT_ENTITY_TYPE, { id });
      return Boolean(entity && entity.isHydrated);
    },
    eventPreviewState() {
      const stored = this.dynamicComponentInstance?.getRootOption?.("event-preview-state");

      return stored || EVENT_PREVIEW_STATE_AUTOMATIC;
    },
  },
  methods: {
    exampleTimingForPreviewState() {
      const now = Math.floor(Date.now() / 1000);
      switch (this.eventPreviewState) {
        case EVENT_PREVIEW_STATE_PRE_EVENT:
          return { startTime: now + 86400, endTime: now + 90000, completedAt: 0 };
        case EVENT_PREVIEW_STATE_ROOM_OPEN:
          return { startTime: now + 300, endTime: now + 3900, completedAt: 0, allowEarlyJoin: true, earlyJoinMinutes: 15 };
        case EVENT_PREVIEW_STATE_LIVE:
          return { startTime: now - 600, endTime: now + 3000, completedAt: 0 };
        case EVENT_PREVIEW_STATE_ENDED:
          return { startTime: now - 7200, endTime: now - 3600, completedAt: now - 3600 };
        default:
          return {};
      }
    },
    getExampleEntity() {
      return {
        "id": 1,
        "title": "Test Event",
        "description": "Test Description",
        "image": "",
        "location": "1234 Example St, Brooklyn NY",
        "meetingLink": "https://zoom.us/j/1234567890",
        "eventType": "webinar",
        "ticketPrice": 0,
        "eventDate": exampleStartTimestamp(),
        "startTime": exampleStartTimestamp(),
        "endTime": exampleStartTimestamp() + EXAMPLE_EVENT_DURATION_SECONDS,
        "recurringFrequency": 3,
        "recurrenceSummary": "Weekly on Tuesday",
        "publication": null,
        "completedAt": 0,
        "alwaysAvailable": false,
        "allowEarlyJoin": true,
        "earlyJoinMinutes": 15,
        "allowEarlyVideo": false,
        "isAllDay": false,
        "saveMeeting": true,
        "chatOpenBeforeMinutes": 30,
        "chatOpenAfterMinutes": 0,
        ...this.exampleTimingForPreviewState(),
      };
    },
    getRealEntity() {
      if (this.isPreviewMode) {
        return this.getExampleEntity();
      }
      const id = this.eventEntityId;
      if (!id) {
        return EMPTY_EVENT_ENTITY;
      }
      const [eventId, occurrenceStart] = String(id).split(OCCURRENCE_KEY_SEPARATOR);
      const entity = App.get(contextualAppName()).getEntity(EVENT_ENTITY_TYPE, { id: eventId });
      if (!entity) {
        return EMPTY_EVENT_ENTITY;
      }

      return projectEntityOntoOccurrence(entity, Number(occurrenceStart));
    },
  },
};
